import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Data, Params } from '@angular/router';
import { IStrapiCollection, IStrapiEntity } from '@core/interfaces/strapi.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { nativeElement } from '@noda-lib/utils/handlers';
import { ArticlesPageImports } from '@pages/articles/articles-page/articles-page.imports';
import { isPageInRange } from '@pages/articles/utils/page-in-range';
import { DEFAULT_LANG } from '@translate/constants/available-langs';
import { LanguageService } from '@translate/services/language.service';
import { merge, Observable, skip, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { IArticle } from '../interfaces/article.interface';
import { ArticlesDataService } from '../services/articles.data.service';

@UntilDestroy()
@Component({
  selector: 'noda-articles-page',
  standalone: true,
  templateUrl: './articles-page.component.html',
  styleUrls: ['./articles-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: ArticlesPageImports,
})
export class ArticlesPageComponent implements OnInit {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private articlesService: ArticlesDataService = inject(ArticlesDataService);
  protected readonly languageService: LanguageService = inject(LanguageService);

  public articlesStartAnchor: Signal<ElementRef<HTMLDivElement>> = viewChild('articlesStartAnchor');
  public articles: WritableSignal<IArticle[]> = signal<IArticle[]>([]);
  public isLoading: Signal<boolean> = computed(() => this.articlesService.isLoading());
  public currentPage: WritableSignal<number> = signal<number>(1);
  private activeTag: WritableSignal<string> = signal<string>(null);
  public pageSize: Signal<number> = computed(() => this.articlesService.pageSize());
  public articlesTotal: Signal<number> = computed(() => this.articlesService.articlesTotal());
  public needPagination: Signal<boolean> = computed(() => this.articlesTotal() > this.pageSize());
  private pagesCount: Signal<number> = computed(() => {
    return Math.ceil(this.articlesTotal() / this.pageSize());
  });

  ngOnInit(): void {
    this.initArticlesData();
  }

  private initArticlesData(): void {
    merge(this.getArticlesFromResolver$(), this.watchArticleTagFilter$())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (articles: IStrapiCollection<IArticle>) => {
          this.articles.set(
            articles.data.slice(1).map((a: IStrapiEntity<IArticle>) => a.attributes),
          );
          this.activeTag.set(this.route.snapshot.queryParams['tag']);
          this.currentPage.set(1);
        },
      });
  }

  private getArticlesFromResolver$(): Observable<IStrapiCollection<IArticle>> {
    return this.route.data.pipe(
      map((data: Data): IStrapiCollection<IArticle> => data['articlesData']),
    );
  }

  private watchArticleTagFilter$(): Observable<IStrapiCollection<IArticle>> {
    return this.route.queryParams.pipe(
      skip(1),
      switchMap((params: Params) => {
        if (this.articlesService.langNeedDefaultTags() === this.languageService.activeLang) {
          return this.articlesService.getArticles({ lang: DEFAULT_LANG, tag: params['tag'] });
        }

        return this.articlesService.getArticles({
          lang: this.languageService.activeLang,
          tag: params['tag'],
        });
      }),
    );
  }

  public handleMobileSwipe(direction: number): void {
    const targetPage: number = this.currentPage() + direction;

    if (isPageInRange(targetPage, this.pagesCount())) {
      this.changePage(targetPage);
    }
  }

  public changePage(page: number): void {
    this.scrollToArticlesStartAnchor();

    this.articlesService
      .getArticlesByPage(page, this.activeTag())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.currentPage.set(page);
          this.articles.set(this.articlesService.articlesCollection()[page]);
        },
      });
  }

  private scrollToArticlesStartAnchor(): void {
    if (this.articlesStartAnchor()) {
      nativeElement(this.articlesStartAnchor()).scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }
}
